import { selectedContextMenuOptionAtom, View } from '../../atoms';

import { useNavParams, useSelector } from 'hooks';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import usePlanningEntity from '../../hooks/usePlanningEntity';
import { getEntityPreferenceKey, getViewContext } from '../../utils';
import { ViewContext } from '../../enums';

enum Options {
  END_APPOINTMENT = 'endAppointment',
  MANAGE_CLIENT_ABSENCE_PERIODS = 'manageClientAbsencePeriods',
  SHOW_PLANNING_CLIENT = 'showPlanningClient',
  NAVIGATE_TO_ECD_CLIENT = 'navigateToECDClient',
  SET_APPOINTMENT_ABSENT = 'setAppointmentAbsent',
  SET_APPOINTMENT_PRESENT = 'setAppointmentPresent',
  EDIT_APPOINTMENT = 'editAppointment',
  COPY_APPOINTMENT = 'copyAppointment',
  DELETE_APPOINTMENT = 'deleteAppointment',
  CREDIT_APPOINTMENT = 'creditAppointment',
  ASSIGN_EMPLOYMENT = 'assignEmployment',
  UNASSIGN_EMPLOYMENT = 'unassignEmployment',
  UNASSIGN_APPOINTMENT_FROM_APPOINTMENT_GROUPING = 'unassignAppointment'
}

interface UseContextMenuOptionsProps {
  eventId: string;
  clientId: string;
  schedulingLabel: string;
  scheduleStart?: string;
  isEditable: boolean;
  isAbsent: boolean;
  eventIsOpen: boolean;
  isAssigned: boolean;
  isProject: boolean;
  isWorkPlace: boolean;
  hasAppointmentGrouping: boolean;
  hasEmployee: boolean;
  isCreditable: boolean;
  isCredit: boolean;
}

export default function useContextMenuOptions({
  eventId,
  clientId,
  hasAppointmentGrouping,
  hasEmployee,
  schedulingLabel,
  scheduleStart,
  isEditable,
  isAbsent,
  eventIsOpen,
  isAssigned,
  isProject,
  isWorkPlace,
  isCreditable,
  isCredit
}: UseContextMenuOptionsProps) {
  const { t } = useTranslation('common', { keyPrefix: 'activityPlanning' });

  const { params } = useNavParams();
  const view = params?.view as View;

  const userDetails = useSelector((state) => state.userDetails);
  const {
    hasActivityPlanningEditAccess,
    hasWorkPlacePlanningEditAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasManageProjectsAccess,
    hasProjectTeamAccess
  } = useSelector((state) => state.grants);

  const ECDUrl = userDetails?.sdbSsoApps?.['SDB ECD'];
  const clientUrl =
    ECDUrl && clientId
      ? `${ECDUrl}/Clients/Client/Open?clientId=${clientId}`
      : '';

  const projectAppointmentEditAccess =
    hasManageProjectsAccess && (schedulingLabel === 'PROJECT' || isProject);
  const workPlaceAppointmentEditAccess =
    hasWorkPlacePlanningEditAccess &&
    schedulingLabel === 'WORKPLACE' &&
    isWorkPlace &&
    getViewContext() == ViewContext.Team;
  const vvAppointmentEditAccess =
    hasActivityPlanningEditAccess && schedulingLabel === 'VV';
  const projectMyActivitiesEditAccess =
    hasMyActivitiesProjectAccess &&
    getViewContext() == ViewContext.Own &&
    isProject;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType =
    planningEntityTypePreference[getEntityPreferenceKey(view)];
  const options = [
    ...(workPlaceAppointmentEditAccess ||
    vvAppointmentEditAccess ||
    ((projectMyActivitiesEditAccess ||
      hasManageOwnProjectsAccess ||
      hasProjectTeamAccess) &&
      isEditable)
      ? [t(Options.EDIT_APPOINTMENT)]
      : []),
    ...((workPlaceAppointmentEditAccess || projectMyActivitiesEditAccess) &&
    (view === View.Roster || view === View.RosterPragmatic) &&
    !isCredit
      ? [t(Options.COPY_APPOINTMENT)]
      : []),
    ...(vvAppointmentEditAccess && !isAbsent && clientId
      ? [t(Options.SET_APPOINTMENT_ABSENT)]
      : []),
    ...(vvAppointmentEditAccess && isAbsent && clientId
      ? [t(Options.SET_APPOINTMENT_PRESENT)]
      : []),
    ...(vvAppointmentEditAccess ? [t(Options.END_APPOINTMENT)] : []),
    ...(workPlaceAppointmentEditAccess ||
    ((projectMyActivitiesEditAccess ||
      hasManageOwnProjectsAccess ||
      hasProjectTeamAccess) &&
      isEditable &&
      isProject)
      ? [t(Options.DELETE_APPOINTMENT)]
      : []),
    ...(vvAppointmentEditAccess &&
    (isAssigned || planningEntityType === 'EMPLOYEE')
      ? [t(Options.UNASSIGN_APPOINTMENT_FROM_APPOINTMENT_GROUPING)]
      : []),
    ...(workPlaceAppointmentEditAccess ||
    (vvAppointmentEditAccess &&
      (view === View.Roster || view === View.RosterPragmatic))
      ? [t(Options.ASSIGN_EMPLOYMENT)]
      : []),
    ...(workPlaceAppointmentEditAccess &&
    !eventIsOpen &&
    (hasAppointmentGrouping || hasEmployee)
      ? [t(Options.UNASSIGN_EMPLOYMENT)]
      : []),
    ...(clientId ? [t(Options.SHOW_PLANNING_CLIENT)] : []),
    ...(clientUrl ? [t(Options.NAVIGATE_TO_ECD_CLIENT)] : []),
    ...(vvAppointmentEditAccess && clientId
      ? [t(Options.MANAGE_CLIENT_ABSENCE_PERIODS)]
      : []),
    ...(projectAppointmentEditAccess
      ? [
          /** project options */
        ]
      : []),
    ...((projectMyActivitiesEditAccess ||
      hasManageOwnProjectsAccess ||
      hasProjectTeamAccess) &&
    isCreditable &&
    isProject
      ? [t(Options.CREDIT_APPOINTMENT)]
      : [])
  ];

  const setContextMenuById = useSetRecoilState(selectedContextMenuOptionAtom);

  const handleOptionClick = useCallback(
    (option: string) => {
      setContextMenuById({
        id: eventId,
        scheduleStart,
        option
      });
    },
    [eventId]
  );

  return {
    options,
    handleOptionClick
  };
}
